import { useCallback } from 'react'
import { POSTHOG_EVENTS, safeCapture, shouldInitializePostHog, usePostHog } from '@/lib/posthog'

/**
 * Survey types supported by the app.
 * Each type maps to a different survey ID with context-specific questions.
 */
export type SurveyType = 'general' | 'feature';

/**
 * Hook for triggering PostHog feedback surveys.
 * 
 * Supports multiple survey types with different questions and frequency limits:
 * - 'general': General product feedback (persistent button, feedback route)
 * - 'feature': Feature-specific feedback (onboarding, queue, tasks, DIY planner, etc.)
 * - 'first_task': First task celebration (queue card after first completion)
 * 
 * @example
 * ```tsx
 * const { showFeedbackSurvey } = useFeedbackSurvey();
 * 
 * // General feedback (persistent button)
 * <Button onClick={() => showFeedbackSurvey('general', 'persistent_button')}>
 *   Send Feedback
 * </Button>
 * 
 * // Feature-specific feedback
 * showFeedbackSurvey('feature', 'feature_used', { 
 *   feature_name: 'queue', 
 *   feature_context: 'first_use' 
 * });
 * 
 * // First task celebration (queue card)
 * showFeedbackSurvey('first_task', 'first_task_completed');
 * ```
 */
export function useFeedbackSurvey() {
  const posthog = usePostHog()
  const mode = import.meta.env.MODE

  const showFeedbackSurvey = useCallback(
    (
      surveyType: SurveyType,
      context?: string,
      properties?: Record<string, unknown>
    ) => {
      const devEnabled = import.meta.env.VITE_POSTHOG_DEV_ENABLED
      if (!posthog || !shouldInitializePostHog(mode, devEnabled)) {
        return;
      }

      // Map survey type to env var and event name
      const surveyConfig: Record<
        SurveyType,
        { surveyId: string | undefined; eventName: string }
      > = {
        general: {
          surveyId: import.meta.env.VITE_PUBLIC_POSTHOG_SURVEY_GENERAL_FEEDBACK,
          eventName: POSTHOG_EVENTS.GENERAL_FEEDBACK_TRIGGERED,
        },
        feature: {
          surveyId: import.meta.env.VITE_PUBLIC_POSTHOG_SURVEY_FEATURE_FEEDBACK,
          eventName: POSTHOG_EVENTS.FEATURE_FEEDBACK_TRIGGERED,
        },
      };

      const config = surveyConfig[surveyType];
      const surveyId = config.surveyId;

      // Always capture the event, even if survey won't show
      // This lets us track survey trigger attempts in analytics
      safeCapture(posthog, config.eventName, {
        context: context || 'manual',
        has_survey_id: !!surveyId,
        ...properties,
      });

      if (!surveyId) {
        console.warn(
          `PostHog survey ID not configured for type "${surveyType}" - event captured but survey will not display`
        );
        return;
      }

      // Trigger the survey
      // PostHog will check if conditions are met (e.g., frequency limits)
      // Cast to any to avoid importing DisplaySurveyType enum from posthog-js
      // (enum imports are runtime values that would pull the SDK into the initial bundle)
      // The actual runtime value of DisplaySurveyType.Popover is 'popover'
      posthog.displaySurvey(surveyId, {
         
        displayType: 'popover' as any,
        ignoreConditions: false, // Respect frequency limits set in PostHog
        ignoreDelay: false, // Respect delay settings in PostHog
      })
    },
    [posthog, mode]
  );

  return { showFeedbackSurvey };
}
